import {identity} from 'lodash-es';
import moment from 'moment';
import {useCallback} from 'react';
import {toast} from 'react-toastify';

import type {AfriexProcessorTxParams} from '@/screens/Processor/Transactions/types';
import type {
  AfriexLogParams,
  AfriexQueryParams,
  BusinessInvoiceListParams,
} from '@/types';
import type {MarketPlaceTradeQuery} from '@/types/MarketPlace';

export type queryParamsType =
  | AfriexQueryParams
  | AfriexLogParams
  | AfriexProcessorTxParams
  | BusinessInvoiceListParams
  | MarketPlaceTradeQuery;
const useFilters = <T extends queryParamsType>(
  setFilters: React.Dispatch<React.SetStateAction<T>>,
) => {
  /**
   * Applies `filters`, or rejects them and applies nothing.
   *
   * Returns whether the filters were applied. Callers must not perform
   * success-only work -- resetting pagination, closing the filter panel -- on a
   * `false`, or the user gets a rejection toast alongside a refetch and a jump
   * back to page one.
   */
  const applyFilters = useCallback(
    (filters: T): boolean => {
      // Start with a safe copy that maintains required fields (like tags)
      const cleanedFilters = {...filters} as Partial<T>;

      // Remove null, undefined, empty string values (similar to pickBy but preserving structure)
      Object.keys(filters).forEach((key) => {
        if (!identity(filters[key as keyof T])) {
          delete cleanedFilters[key as keyof T];
        }
      });

      // An invalid date range applies nothing at all. Previously the dates were
      // deleted and the remaining filters applied anyway, so the user saw an
      // error toast AND a refreshed list that had silently dropped the range
      // they asked for -- worst when another filter was set too, because the
      // result then looked filtered but was not date-bounded. Returning is safe
      // because this hook never touches the staging state the panel renders, so
      // the typed values stay put and the user can correct the date and
      // re-apply without re-entering anything.
      if (cleanedFilters.fromDate && cleanedFilters.toDate) {
        const isValidRange = moment(cleanedFilters.toDate).isSameOrAfter(
          moment(cleanedFilters.fromDate),
        );

        if (!isValidRange) {
          toast.error('From Date cannot be after To Date');
          return false;
        }

        cleanedFilters.fromDate = moment(cleanedFilters.fromDate)
          .startOf('day')
          .toISOString();

        cleanedFilters.toDate = moment(cleanedFilters.toDate)
          .endOf('day')
          .toISOString();
      } else if (cleanedFilters.fromDate || cleanedFilters.toDate) {
        toast.error('Both From Date and To Date are required');
        return false;
      }

      setFilters(cleanedFilters as T);

      return true;
    },
    [setFilters],
  );

  return {applyFilters};
};

export default useFilters;
